import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { Search, Filter, X, Mic, MicOff, Star, Truck, Grid as GridIcon, List, Info } from 'lucide-react';
import { Container, Grid, Card, Badge, Button } from '../../styles/GlobalStyles';
import { menuCategories, fryFlavors, shakes } from '../../data/menuData';
import { MenuItem as MenuItemType } from '../../types';
import MenuItem from './MenuItem';
import { useVoiceSearch, useVoiceCommands } from '../../hooks/useVoiceSearch';
import { usePullToRefresh } from '../../hooks/useSwipeGestures';
import { useDebounce } from '../../hooks/usePerformanceOptimizations';
import LoadingSpinner from '../LoadingSpinner';

const MenuSection = styled.section`
  padding: 4rem 0;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%);
  min-height: 100vh;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(139, 0, 0, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 215, 0, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(40, 167, 69, 0.03) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  z-index: 2;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  
  .signature {
    background: linear-gradient(135deg, #8B0000, #FFD700);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-shadow: none;
    filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3));
  }
  
  .burgers {
    background: linear-gradient(135deg, #8B4513, #A0522D);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-shadow: none;
    filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3));
  }
`;

const SectionDescription = styled.p`
  font-size: 1.125rem;
  color: #2d3748;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  font-style: italic;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.9);
  padding: 1rem 2rem;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
`;

const CategorySection = styled.div`
  margin-bottom: 4rem;
  position: relative;
  z-index: 2;
`;

const CategoryHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const CategoryTitle = styled.h3.withConfig({
  shouldForwardProp: (prop) => prop !== 'variant'
})<{ variant?: string }>`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  padding: 1rem 2rem;
  border-radius: 20px;
  text-align: center;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  
  background: ${props => {
    switch (props.variant) {
      case 'bites': return 'linear-gradient(135deg, #ff8c00, #ff6b35)';
      case 'chicken': return 'linear-gradient(135deg, #ffc107, #ff8f00)';
      case 'greens': return 'linear-gradient(135deg, #28a745, #20c997)';
      case 'shakes': return 'linear-gradient(135deg, #e91e63, #ad1457)';
      default: return 'linear-gradient(135deg, #8B0000, #FFD700)';
    }
  }};
`;

const CategoryDescription = styled.p`
  color: #2d3748;
  max-width: 800px;
  margin: 0 auto 2rem auto;
  line-height: 1.6;
  font-size: 1rem;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.95);
  padding: 1.5rem 2rem;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  text-align: center;
`;

const CustomizationSection = styled.div`
  margin: 3rem 0;
  text-align: center;
`;

const CustomizationTitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #1a202c;
  background: rgba(255, 255, 255, 0.95);
  padding: 1.5rem 2rem;
  border-radius: 20px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  text-shadow: none;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 2rem;
`;

const CustomizationGrid = styled(Grid)`
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  max-width: 800px;
  margin: 0 auto;
`;

const CustomizationCard = styled(Card).withConfig({
  shouldForwardProp: (prop) => prop !== 'borderColor'
})<{ borderColor: string }>`
  text-align: center;
  border: 2px solid ${props => props.borderColor};
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-4px);
  }
`;

const CustomizationIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const CustomizationName = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: #ffffff;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
`;

const CustomizationDesc = styled.p`
  font-size: 0.875rem;
  color: #e2e8f0;
  margin: 0;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
`;

const FryFlavorCard = styled(Card).withConfig({
  shouldForwardProp: (prop) => prop !== 'bgColor'
})<{ bgColor: string }>`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  position: relative;
  overflow: hidden;
  background: ${props => props.bgColor};
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.1);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
    border-color: rgba(255, 255, 255, 0.6);
    
    &::before {
      opacity: 1;
    }
  }
`;

const FlavorIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 0.5rem;
  filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3));
  transition: transform 0.3s ease;
  
  ${FryFlavorCard}:hover & {
    transform: scale(1.1) rotate(5deg);
  }
`;

const FlavorName = styled.h4`
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
`;

const FlavorDesc = styled.p`
  font-size: 0.875rem;
  opacity: 0.9;
  margin: 0;
  flex: 1;
  font-weight: 500;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
`;

const FlavorCategory = styled(Badge)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 0.7rem;
  padding: 0.25rem 0.75rem;
  background: rgba(255, 255, 255, 0.95);
  color: #1a202c;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const ShakeGrid = styled(Grid)`
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
`;

const ShakeCard = styled(Card).withConfig({
  shouldForwardProp: (prop) => prop !== 'flavorTheme'
})<{ flavorTheme?: string }>`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  position: relative;
  overflow: hidden;
  background: ${props => {
    switch (props.flavorTheme) {
      case 'vanilla': return 'linear-gradient(135deg, #FEF3C7 0%, #F59E0B 100%)';
      case 'strawberry': return 'linear-gradient(135deg, #FECACA 0%, #FFD700 100%)';
      case 'fruity': return 'linear-gradient(135deg, #DDD6FE 0%, #8B5CF6 100%)';
      case 'cinnamon': return 'linear-gradient(135deg, #FED7AA 0%, #EA580C 100%)';
      case 'berry': return 'linear-gradient(135deg, #DBEAFE 0%, #3B82F6 100%)';
      case 'chocolate': return 'linear-gradient(135deg, #D1FAE5 0%, #059669 100%)';
      case 'cookie': return 'linear-gradient(135deg, #FEF3C7 0%, #D97706 100%)';
      default: return props.theme.colors.cardBackground;
    }
  }};
  border: 2px solid rgba(255, 255, 255, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.1);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
    border-color: rgba(255, 255, 255, 0.6);
    
    &::before {
      opacity: 1;
    }
  }
`;

const ShakeIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 0.5rem;
  filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3));
  transition: transform 0.3s ease;
  
  ${ShakeCard}:hover & {
    transform: scale(1.1) rotate(5deg);
  }
`;

const ShakeName = styled.h4`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
  text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.8);
`;

const ShakeDesc = styled.p`
  color: #374151;
  margin: 0;
  flex: 1;
  font-weight: 500;
  text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.6);
`;

const ShakePrice = styled.div`
  font-size: 1.4rem;
  font-weight: 800;
  color: #8B0000;
  margin-bottom: 16px;
  text-shadow: 0 2px 4px rgba(139, 0, 0, 0.2);
  display: none; /* Hide price */
`;

const ShakeBadge = styled(Badge)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 0.7rem;
  padding: 0.25rem 0.75rem;
  background: rgba(255, 255, 255, 0.95);
  color: #1a202c;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const SearchAndFilterBar = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  margin-bottom: 3rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 2rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  
  &:focus-within {
    border-color: rgba(220, 38, 38, 0.3);
    box-shadow: 0 4px 20px rgba(220, 38, 38, 0.15);
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.2s ease;
  background: #f8fafc;
  
  &:focus {
    outline: none;
    border-color: #DC2626;
    background: white;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }
  
  &::placeholder {
    color: #a0aec0;
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  z-index: 2;
`;

const VoiceButton = styled.button<{ isListening: boolean }>`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 12px;
  background: ${props => props.isListening 
            ? 'linear-gradient(135deg, #8B0000, #FFD700)' 
    : 'rgba(107, 114, 128, 0.1)'
  };
  color: ${props => props.isListening ? 'white' : '#6B7280'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.isListening 
              ? 'linear-gradient(135deg, #FFD700, #FFC300)' 
      : 'rgba(107, 114, 128, 0.2)'
    };
    transform: translateY(-50%) scale(1.05);
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  ${props => props.isListening && `
    animation: pulse 2s infinite;
    
    @keyframes pulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
      50% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
    }
  `}
`;

const VoiceTranscript = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 12px;
  padding: 12px 16px;
  margin-top: 8px;
  border: 1px solid rgba(220, 38, 38, 0.2);
  font-size: 0.875rem;
  color: #374151;
  z-index: 10;
  transform: ${props => props.isVisible ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.95)'};
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: all 0.2s ease;
  pointer-events: ${props => props.isVisible ? 'auto' : 'none'};
  
  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 20px;
    width: 12px;
    height: 12px;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(220, 38, 38, 0.2);
    border-bottom: none;
    border-right: none;
    transform: rotate(45deg);
  }
`;

const FilterTags = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterTag = styled.button<{ active: boolean }>`
  padding: 0.5rem 1rem;
  border: 2px solid ${props => props.active ? '#DC2626' : '#e2e8f0'};
  background: ${props => props.active ? '#DC2626' : 'white'};
  color: ${props => props.active ? 'white' : '#4a5568'};
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    border-color: #DC2626;
    background: ${props => props.active ? '#B91C1C' : '#FEF2F2'};
  }
`;

const ResultsCount = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  color: #4a5568;
  font-size: 1rem;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #a0aec0;
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #4a5568;
  }
  
  p {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const PullToRefreshIndicator = styled.div<{ pullDistance: number; isRefreshing: boolean }>`
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%) translateY(${props => Math.min(props.pullDistance - 60, 40)}px);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 0 0 16px 16px;
  padding: 12px 24px;
  border: 1px solid rgba(220, 38, 38, 0.2);
  border-top: none;
  z-index: 1000;
  opacity: ${props => props.pullDistance > 40 ? 1 : 0};
  transition: opacity 0.2s ease;
  
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
`;

// View Toggle Components
const ViewToggleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  background: rgba(255, 255, 255, 0.95);
  padding: 1rem 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const ViewModeText = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ViewToggleButtons = styled.div`
  display: flex;
  background: rgba(107, 114, 128, 0.1);
  border-radius: 12px;
  padding: 4px;
  border: 1px solid rgba(107, 114, 128, 0.2);
`;

const ViewToggleButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  background: ${props => props.active 
            ? 'linear-gradient(135deg, #8B0000, #FFD700)' 
    : 'transparent'
  };
  color: ${props => props.active ? 'white' : '#4a5568'};
  
  &:hover {
    background: ${props => props.active 
              ? 'linear-gradient(135deg, #FFD700, #FFC300)' 
      : 'rgba(107, 114, 128, 0.1)'
    };
    transform: translateY(-1px);
  }
`;

// List View Components (Horizontal Card Layout)
const ListGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ListItem = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.1),
    0 2px 8px rgba(0, 0, 0, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  will-change: transform;
  display: flex;
  align-items: stretch;
  min-height: 180px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    transition: left 0.5s;
    z-index: 1;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.15),
      0 8px 16px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    border-color: rgba(255, 255, 255, 0.4);

    &::before {
      left: 100%;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    min-height: auto;
  }
`;

const ListItemImageContainer = styled.div`
  position: relative;
  width: 180px;
  min-height: 180px;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  flex-shrink: 0;
  border-radius: 12px 0 0 12px;

  @media (max-width: 768px) {
    width: 100%;
    height: 220px;
    border-radius: 12px 12px 0 0;
  }
`;

const ListItemImage = styled.img<{ $loaded?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  opacity: ${props => props.$loaded !== false ? 1 : 0};
  filter: brightness(1.1) contrast(1.05) saturate(1.1);

  ${ListItem}:hover & {
    transform: scale(1.05);
    filter: brightness(1.2) contrast(1.1) saturate(1.2);
  }
`;

const ListItemImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const ListItemContent = styled.div`
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const ListItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const ListItemTitle = styled.h3`
  font-size: 1.35rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 0.25rem 0;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ListItemPrice = styled.span`
  font-size: 1.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #8B0000, #FFD700);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 2px 4px rgba(139, 0, 0, 0.3);
  display: none; /* Hide price */
`;

const ListItemDescription = styled.p`
  color: #4a5568;
  line-height: 1.4;
  margin: 0.5rem 0 1rem 0;
  font-size: 0.9rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ListItemFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  gap: 1rem;
`;

const ListItemTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  flex: 1;
`;

const ListItemTag = styled.span`
  background: linear-gradient(135deg, #8B0000, #FFD700);
  color: white;
  padding: 0.25rem 0.6rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 8px rgba(139, 0, 0, 0.3);
`;

const ListOrderButton = styled.button`
  background: linear-gradient(135deg, #8B0000, #FFD700);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  box-shadow: 0 4px 15px rgba(139, 0, 0, 0.3);
  flex-shrink: 0;
  
  &:hover {
    background: linear-gradient(135deg, #FFD700, #FFC300);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 215, 0, 0.4);
  }

  &:active {
    transform: translateY(-1px);
  }
`;

const Menu: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [visibleCategories, setVisibleCategories] = useState<string[]>(['signature-burgers']);

  // Debounce search term for better performance
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Voice search integration
  const { processVoiceCommand } = useVoiceCommands();
  
  const handleVoiceResult = useCallback((transcript: string) => {
    const command = processVoiceCommand(transcript);
    
    if (command) {
      switch (command.action) {
        case 'search':
          if (command.query) {
            setSearchTerm(command.query);
          }
          break;
        case 'filter':
          if (command.category) {
            setSelectedCategory(command.category);
          }
          break;
        case 'clear':
          setSearchTerm('');
          setSelectedCategory('all');
          setSelectedTags([]);
          break;
      }
    } else {
      // If no specific command, treat as search
      setSearchTerm(transcript);
    }
  }, [processVoiceCommand]);

  const {
    isListening,
    isSupported,
    transcript,
    interimTranscript,
    startListening,
    stopListening
  } = useVoiceSearch({
    onResult: handleVoiceResult,
            onError: (error) => {
          // Handle voice search error silently in production
          if (process.env.NODE_ENV === 'development') {
            console.error('Voice search error:', error);
          }
        }
  });

  // Pull to refresh functionality
  const handleRefresh = useCallback(async () => {
    // Simulate refresh delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedTags([]);
  }, []);

  const { elementRef, isRefreshing, pullDistance } = usePullToRefresh(handleRefresh);

  // Memoize all items to prevent recalculation
  const allItems = useMemo(() => {
    return menuCategories.flatMap(category => category.items);
  }, []);

  // Memoize filtered items with debounced search
  const filteredItems = useMemo(() => {
    let items = allItems;

    // Search filter with debounced term
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      items = items.filter(item =>
        item.name.toLowerCase().includes(searchLower) ||
        item.description.toLowerCase().includes(searchLower) ||
        item.tags?.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    // Category filters
    if (selectedCategory !== 'all') {
      items = items.filter(item =>
        item.category.toLowerCase() === selectedCategory.toLowerCase()
      );
    }

    // Tag filters
    if (selectedTags.length > 0) {
      items = items.filter(item => {
        return selectedTags.some(tag => {
          // Check if it's a category filter
          if (['signature-burgers', 'royale-bites', 'chicken-sandwiches', 'salads'].includes(tag)) {
            return item.category === tag;
          }
          // Check if it's a tag filter
          return item.tags?.includes(tag);
        });
      });
    }

    return items;
  }, [allItems, debouncedSearchTerm, selectedCategory, selectedTags]);

  // Removed handleAddToOrder - now using ingredient showcase approach

  const toggleFilter = useCallback((filter: string) => {
    setSelectedTags(prev =>
      prev.includes(filter)
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  }, []);

  const toggleCategoryVisibility = useCallback((categoryId: string) => {
    setVisibleCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedTags([]);
    setSearchTerm('');
    setSelectedCategory('all');
    setVisibleCategories(['signature-burgers']); // Reset to show only burgers
  }, []);

  const getColorForCategory = useCallback((category: string) => {
    switch (category.toLowerCase()) {
      case 'gourmet': return '#10B981';
      case 'classic': return '#6B7280';
      case 'spicy': return '#EF4444';
      case 'sweet': return '#F59E0B';
      default: return '#8B5CF6';
    }
  }, []);

  // Render list item for list view
  const renderListItem = useCallback((item: MenuItemType) => (
    <ListItem key={item.id}>
      <ListItemImageContainer>
        {item.image ? (
          <ListItemImage src={item.image} alt={item.name} />
        ) : (
          <ListItemImagePlaceholder>🍔</ListItemImagePlaceholder>
        )}
      </ListItemImageContainer>
      <ListItemContent>
        <ListItemHeader>
          <ListItemTitle>{item.name}</ListItemTitle>
          {/* <ListItemPrice>${item.price.toFixed(2)}</ListItemPrice> */}
        </ListItemHeader>
        <ListItemDescription>{item.description}</ListItemDescription>
        <ListItemFooter>
          {item.tags && item.tags.length > 0 && (
            <ListItemTags>
              {item.tags.slice(0, 2).map((tag) => (
                <ListItemTag key={tag}>{tag}</ListItemTag>
              ))}
            </ListItemTags>
          )}
          <ListOrderButton>
            <Info size={16} />
            View Details
          </ListOrderButton>
        </ListItemFooter>
      </ListItemContent>
    </ListItem>
  ), []);

  return (
    <MenuSection id="menu" ref={elementRef}>
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing}>
        {isRefreshing ? (
          <>
            <LoadingSpinner variant="spinner" />
            Refreshing menu...
          </>
        ) : (
          <>
            ↓ Pull to refresh
          </>
        )}
      </PullToRefreshIndicator>
      <Container>
        <SectionHeader>
          <SectionTitle>
            <span className="signature">SIGNATURE</span> <span className="burgers">BURGERS</span>
          </SectionTitle>
          <SectionDescription>
            Each burger is crafted with premium ingredients and served with AI-optimized precision
          </SectionDescription>
        </SectionHeader>

        <SearchAndFilterBar>
          <SearchContainer>
            <SearchInputWrapper>
              <SearchInput
                type="text"
                placeholder="Search burgers, ingredients, or dietary preferences..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <SearchIcon>
                <Search size={20} />
              </SearchIcon>
              
              {isSupported && (
                <VoiceButton
                  isListening={isListening}
                  onClick={isListening ? stopListening : startListening}
                  title={isListening ? 'Stop voice search' : 'Start voice search'}
                >
                  {isListening ? <MicOff size={20} /> : <Mic size={20} />}
                </VoiceButton>
              )}
              
              <VoiceTranscript isVisible={!!(transcript || interimTranscript)}>
                {transcript || interimTranscript || 'Listening...'}
              </VoiceTranscript>
            </SearchInputWrapper>
          </SearchContainer>
          
          <FilterTags>
            <span style={{ color: '#4a5568', fontWeight: '600', marginRight: '0.5rem' }}>
              <Filter size={16} style={{ marginRight: '0.5rem' }} />
              Menu Categories:
            </span>
            
            <FilterTag
              active={visibleCategories.includes('signature-burgers')}
              onClick={() => toggleCategoryVisibility('signature-burgers')}
            >
              🍔 Burgers
            </FilterTag>
            
            <FilterTag
              active={visibleCategories.includes('royale-bites')}
              onClick={() => toggleCategoryVisibility('royale-bites')}
            >
              🍟 Bites
            </FilterTag>
            
            <FilterTag
              active={visibleCategories.includes('chicken-sandwiches')}
              onClick={() => toggleCategoryVisibility('chicken-sandwiches')}
            >
              🐔 Chicken
            </FilterTag>
            
            <FilterTag
              active={visibleCategories.includes('salads')}
              onClick={() => toggleCategoryVisibility('salads')}
            >
              🥗 Salads
            </FilterTag>
            
            <FilterTag
              active={visibleCategories.includes('fries')}
              onClick={() => toggleCategoryVisibility('fries')}
            >
              🍟 Fries
            </FilterTag>
            
            <FilterTag
              active={visibleCategories.includes('shakes')}
              onClick={() => toggleCategoryVisibility('shakes')}
            >
              🥤 Shakes
            </FilterTag>
            
            {/* Item-level filters when searching */}
            {searchTerm && (
              <>
                <FilterTag
                  active={selectedTags.includes('CLASSIC')}
                  onClick={() => toggleFilter('CLASSIC')}
                >
                  ⭐ Classic
                </FilterTag>
                
                <FilterTag
                  active={selectedTags.includes('MILD-MEDIUM HEAT')}
                  onClick={() => toggleFilter('MILD-MEDIUM HEAT')}
                >
                  🌶️ Spicy
                </FilterTag>
                
                <FilterTag
                  active={selectedTags.includes('BBQ SPECIALTY')}
                  onClick={() => toggleFilter('BBQ SPECIALTY')}
                >
                  🔥 BBQ
                </FilterTag>
                
                <FilterTag
                  active={selectedTags.includes('FRESH')}
                  onClick={() => toggleFilter('FRESH')}
                >
                  🥬 Fresh
                </FilterTag>
              </>
            )}
            
            {(visibleCategories.length > 1 || searchTerm || selectedTags.length > 0) && (
              <FilterTag
                active={false}
                onClick={clearFilters}
                style={{ background: '#f56565', borderColor: '#f56565', color: 'white' }}
              >
                <X size={14} />
                Reset
              </FilterTag>
            )}
          </FilterTags>
        </SearchAndFilterBar>

        {/* View Toggle */}
        <ViewToggleContainer>
          <ViewModeText>
            View Menu
          </ViewModeText>
          <ViewToggleButtons>
            <ViewToggleButton 
              active={viewMode === 'grid'} 
              onClick={() => setViewMode('grid')}
            >
              <GridIcon size={16} />
              Card View
            </ViewToggleButton>
            <ViewToggleButton 
              active={viewMode === 'list'} 
              onClick={() => setViewMode('list')}
            >
              <List size={16} />
              List View
            </ViewToggleButton>
          </ViewToggleButtons>
        </ViewToggleContainer>

        {(searchTerm || selectedTags.length > 0) && (
          <ResultsCount>
            {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''} found
            {searchTerm && ` for "${searchTerm}"`}
          </ResultsCount>
        )}

        {/* Show filtered results when searching/filtering */}
        {(searchTerm || selectedTags.length > 0) && (
          <>
            {filteredItems.length === 0 ? (
              <NoResults>
                <h3>🔍 No items found</h3>
                <p>Try adjusting your search or filters</p>
                <Button onClick={clearFilters}>Clear Filters</Button>
              </NoResults>
            ) : viewMode === 'grid' ? (
              <Grid>
                {filteredItems.map((item) => (
                  <MenuItem 
                    key={item.id} 
                    item={item}
                  />
                ))}
              </Grid>
            ) : (
              <ListGrid>
                {filteredItems.map(renderListItem)}
              </ListGrid>
            )}
          </>
        )}

        {/* Show visible categories when not searching/filtering */}
        {!searchTerm && selectedTags.length === 0 && (
          <>
            {menuCategories
              .filter(category => visibleCategories.includes(category.id))
              .map((category) => (
                <CategorySection key={category.id}>
                  <CategoryHeader>
                    <CategoryTitle variant={
                      category.id === 'royale-bites' ? 'bites' :
                      category.id === 'chicken-sandwiches' ? 'chicken' :
                      category.id === 'salads' ? 'greens' : 'default'
                    }>
                      {category.name === 'Royale Greens' && 'ROYALE '}
                      {category.name === 'Royale Greens' ? 'GREENS' : category.name.toUpperCase()}
                    </CategoryTitle>
                    {category.description && (
                      <CategoryDescription>{category.description}</CategoryDescription>
                    )}
                  </CategoryHeader>
                  {viewMode === 'grid' ? (
                    <Grid>
                      {category.items.map((item) => (
                        <MenuItem 
                          key={item.id} 
                          item={item}
                        />
                      ))}
                    </Grid>
                  ) : (
                    <ListGrid>
                      {category.items.map(renderListItem)}
                    </ListGrid>
                  )}
                </CategorySection>
              ))}

            {/* Show Fries section when visible */}
            {visibleCategories.includes('fries') && (
              <CategorySection>
                <CategoryHeader>
                  <CategoryTitle>ROYALE FRIES</CategoryTitle>
                </CategoryHeader>
                <ShakeGrid>
                  {fryFlavors.map((flavor, index) => {
                    const badgeVariants: ('primary' | 'secondary' | 'accent' | 'warning' | 'info')[] = ['info', 'warning', 'accent', 'secondary'];
                    
                    return (
                      <FryFlavorCard 
                        key={flavor.id}
                        bgColor={getColorForCategory(flavor.category)}
                      >
                        <FlavorCategory variant={badgeVariants[index]}>{flavor.category}</FlavorCategory>
                        <div style={{ 
                          position: 'relative', 
                          marginBottom: '1rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {flavor.image ? (
                            <img 
                              src={flavor.image} 
                              alt={flavor.name}
                              style={{
                                width: '140px',
                                height: '140px',
                                objectFit: 'cover',
                                borderRadius: '50%',
                                border: '4px solid rgba(255, 255, 255, 0.9)',
                                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.25)',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.transform = 'scale(1.05)';
                                e.currentTarget.style.boxShadow = '0 15px 35px rgba(0, 0, 0, 0.3)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.25)';
                              }}
                              onError={(e) => {
                                // Fallback to icon if image fails to load
                                const target = e.currentTarget;
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = `<div style="font-size: 4rem; filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3));">${flavor.icon}</div>`;
                                }
                              }}
                            />
                          ) : (
                            <FlavorIcon>{flavor.icon}</FlavorIcon>
                          )}
                        </div>
                        <FlavorName>{flavor.name}</FlavorName>
                        <FlavorDesc>{flavor.description}</FlavorDesc>
                      </FryFlavorCard>
                    );
                  })}
                </ShakeGrid>
              </CategorySection>
            )}
          </>
        )}

        {/* Show Shakes section when visible */}
        {visibleCategories.includes('shakes') && (
          <CategorySection>
            <CategoryHeader>
              <CategoryTitle variant="shakes">
                ROYALE <span style={{color: '#EC4899'}}>CUSTARD SHAKES</span>
              </CategoryTitle>
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '2rem',
                marginBottom: '2rem',
                flexWrap: 'wrap'
              }}>
                <Badge variant="info">THICK & CREAMY<br />Real custard base</Badge>
                <Badge variant="warning">UNIQUE FLAVORS<br />Cereal & classic blends</Badge>
                <Badge variant="secondary">PREMIUM QUALITY<br />Made fresh daily</Badge>
              </div>
              <div style={{
                background: '#FEF3C7',
                color: '#92400E',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '2rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                justifyContent: 'center'
              }}>
                ⚠️ <strong>Allergy Notice:</strong> Our shakes may contain nuts and tree nuts.
              </div>
            </CategoryHeader>
            <ShakeGrid>
              {shakes.map((shake, index) => {
                const flavorThemes = ['vanilla', 'strawberry', 'fruity', 'cinnamon', 'berry', 'chocolate', 'cookie'];
                const badges = ['CLASSIC', 'POPULAR', 'KIDS LOVE', 'SEASONAL', 'SIGNATURE', 'RICH', 'PREMIUM'];
                const badgeVariants: ('primary' | 'secondary' | 'accent' | 'warning' | 'info')[] = ['warning', 'accent', 'info', 'secondary', 'primary', 'accent', 'warning'];
                
                return (
                  <ShakeCard key={shake.id} flavorTheme={flavorThemes[index]}>
                    <ShakeBadge variant={badgeVariants[index]}>{badges[index]}</ShakeBadge>
                    <div style={{ 
                      position: 'relative', 
                      marginBottom: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {shake.image ? (
                        <img 
                          src={shake.image} 
                          alt={shake.name}
                          style={{
                            width: '140px',
                            height: '140px',
                            objectFit: 'cover',
                            borderRadius: '50%',
                            border: '4px solid rgba(255, 255, 255, 0.9)',
                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                            transition: 'all 0.3s ease',
                            padding: '8px'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 15px 35px rgba(0, 0, 0, 0.3)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.25)';
                          }}
                          onError={(e) => {
                            // Fallback to icon if image fails to load
                            const target = e.currentTarget;
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = `<div style="font-size: 4rem; filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3));">${shake.icon}</div>`;
                            }
                          }}
                        />
                      ) : (
                        <ShakeIcon>{shake.icon}</ShakeIcon>
                      )}
                    </div>
                    <ShakeName>{shake.name}</ShakeName>
                    <ShakeDesc>{shake.description}</ShakeDesc>
                    {/* <ShakePrice>${shake.price.toFixed(2)}</ShakePrice> */}
                  </ShakeCard>
                );
              })}
            </ShakeGrid>
          </CategorySection>
        )}

        {/* DoorDash Delivery Banner */}
        <CategorySection style={{ marginTop: '4rem' }}>
          <div style={{
            background: '#FF3008', // DoorDash red
            color: 'white',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '16px', // DoorDash style border radius
            maxWidth: '800px',
            margin: '0 auto',
            boxShadow: '0 8px 32px rgba(255, 48, 8, 0.3), 0 4px 16px rgba(0, 0, 0, 0.1)'
          }}>
            {/* DoorDash pattern background */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
              opacity: 0.6
            }} />
            
            <div style={{ position: 'relative', zIndex: 2, padding: '2.5rem 2rem' }}>
              {/* DoorDash logo area */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  background: 'white',
                  color: '#FF3008',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Truck size={20} />
                  DoorDash
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: 'rgba(255, 255, 255, 0.15)',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '0.9rem'
                }}>
                  <Star size={16} fill="currentColor" />
                  4.2 • 100+ ratings
                </div>
              </div>
              
              <h2 style={{
                fontSize: '2.2rem',
                fontWeight: '700',
                marginBottom: '1rem',
                lineHeight: '1.3',
                fontFamily: 'system-ui, -apple-system, sans-serif' // DoorDash font style
              }}>
                Get Royale with Cheese Delivered
              </h2>
              
              <p style={{
                fontSize: '1rem',
                marginBottom: '2rem',
                opacity: 0.9,
                maxWidth: '500px',
                marginLeft: 'auto',
                marginRight: 'auto',
                lineHeight: '1.5'
              }}>
                Craving our signature burgers? Get them delivered through DoorDash with featured items and customer favorites!
              </p>
              
              <div style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'center',
                flexWrap: 'wrap',
                marginBottom: '1.5rem'
              }}>
                <a 
                  href="https://www.doordash.com/store/royale-with-cheese-dearborn-heights-27924828/33501149/?event_type=autocomplete&pickup=false"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: 'white',
                    color: '#FF3008',
                    border: 'none',
                    fontWeight: '700',
                    fontSize: '1rem',
                    padding: '1rem 2rem',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#f8f9fa';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.transform = 'translateY(0px)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  🍔 Order on DoorDash
                </a>
              </div>
              
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '2rem',
                flexWrap: 'wrap',
                fontSize: '0.85rem',
                opacity: 0.85
              }}>
                <span>• Featured Items</span>
                <span>• Customer Favorites</span>
                <span>• Fast Delivery</span>
              </div>
            </div>
          </div>
        </CategorySection>
      </Container>
    </MenuSection>
  );
};

export default Menu; 